import React, { useState, useEffect, useRef } from 'react';
import Loading from '../Common/Loading';

// --- Interfaces ---
interface QuestionStat {
    perc: number;
    total: number;
    display_text: string;
}

interface GridRow {
    lesson_code: string;
    lesson_name: string;
    avg_score: number;
    questions: Record<string, QuestionStat>;
}

const EL_Testperformance: React.FC = () => {
    const API_BASE = process.env.REACT_APP_DOCUMENT_LINK || 'https://staging.sysadmin.elearnoncloud.com/';

    // --- State ---
    const [country, setCountry] = useState('Australia');
    const [dics, setDics] = useState<string[]>([]);
    const [selectedDic, setSelectedDic] = useState('--Select--');
    const [tests, setTests] = useState<string[]>([]);
    const [selectedTest, setSelectedTest] = useState('');
    const [manualTest, setManualTest] = useState('');

    const getFormattedDate = (date: Date) => date.toISOString().split('T')[0];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const [fromDate, setFromDate] = useState(getFormattedDate(yesterday));
    const [toDate, setToDate] = useState(getFormattedDate(new Date()));
    const [appliedFromDate, setAppliedFromDate] = useState(fromDate);   // ADD
    const [appliedToDate, setAppliedToDate] = useState(toDate);         // ADD

    const [threshold, setThreshold] = useState(70);
    const [famWise, setFamWise] = useState(true);

    const [gridData, setGridData] = useState<GridRow[]>([]);
    const [testNameInfo, setTestNameInfo] = useState('');
    const [testCodeInfo, setTestCodeInfo] = useState('');
    const [overallAvg, setOverallAvg] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [selectedRowCode, setSelectedRowCode] = useState<string | null>(null);

    // For measuring first header row height to fix sticky gap
    const [firstRowHeight, setFirstRowHeight] = useState<number>(0);
    const firstHeaderRowRef = useRef<HTMLTableRowElement>(null);

    const MAX_QUESTIONS = 100;
    const columns = Array.from({ length: MAX_QUESTIONS }, (_, i) => String(i + 1).padStart(3, '0'));

    useEffect(() => {
        document.body.style.backgroundColor = '#fff';
    }, []);

    // Measure first header row height after data loads
    useEffect(() => {
        if (firstHeaderRowRef.current) {
            setFirstRowHeight(firstHeaderRowRef.current.getBoundingClientRect().height);
        }
    }, [gridData, hasSearched]);

    // --- UI Colors ---
    const colors = {
        headBg: '#c7ddfd', headText: '#2859ac', border: '#80a0ff',
        rowOdd: '#f4faff', rowEven: '#fff', rowSelected: 'orange',
        failBg: 'red', failText: 'black', avgRowBg: '#c1bbef', avgRowText: '#2859ac'
    };

    // Header th — full border, sticky row 1
    const thBase: React.CSSProperties = {
        backgroundColor: colors.headBg,
        color: colors.headText,
        border: `1px solid ${colors.border}`,
        padding: '4px 6px',
        textAlign: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 3,
        whiteSpace: 'nowrap'
    };

    // Sub-header (question numbers) — sticky row 2, top = measured first row height
    const thSubBase: React.CSSProperties = {
        backgroundColor: colors.headBg,
        color: colors.headText,
        border: `1px solid ${colors.border}`,
        padding: '4px 6px',
        textAlign: 'center',
        position: 'sticky',
        top: firstRowHeight > 0 ? `${firstRowHeight}px` : '31px',
        zIndex: 3,
        whiteSpace: 'nowrap'
    };

    // Body td — right border only, 8px padding
    const tdBase: React.CSSProperties = {
        borderRight: `1px solid ${colors.border}`,
        borderLeft: 'none',
        borderTop: 'none',
        borderBottom: 'none',
        padding: '8px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        fontSize: '13px'
    };

    // --- Effects ---

    // Fetch DICs
    useEffect(() => {
        const fetchDics = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/q_perf_get_dics?country=${country}`);
                const json = await res.json();
                setDics(json.dics || []);
                setSelectedDic('--Select--');
                setTests([]);
                setGridData([]);
                setHasSearched(false);
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };
        fetchDics();
    }, [country]);

    // Fetch Tests when DIC changes
    useEffect(() => {
        const fetchTests = async () => {
            if (selectedDic === '--Select--') return;
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}api/t_perf_get_tests?dic_name=${selectedDic}`);
                const json = await res.json();
                const testList = json.tests || [];
                setTests(testList);

                if (testList.length > 0) {
                    setSelectedTest(testList[0]);
                    setManualTest(testList[0]);   // <-- Add this
                } else {
                    setSelectedTest('');
                    setManualTest('');            // <-- Add this
                }
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        };
        fetchTests();
    }, [selectedDic]);

    // --- Handlers ---
    const handleGo = async () => {
        const targetTest = manualTest.trim() || selectedTest;
        if (!targetTest || selectedDic === '--Select--') {
            alert("Please select a Dicfile and provide a Testcode.");
            return;
        }
        setLoading(true);
        setHasSearched(true);
        setSelectedRowCode(null);
        try {
            const res = await fetch(`${API_BASE}api/t_perf_get_data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    country,
                    dic_code: selectedDic,
                    test_code: targetTest,
                    from_date: fromDate,
                    to_date: toDate
                })
            });
            const json = await res.json();
            if (json.error) {
                alert(json.error);
            } else {
                setGridData(json.results || []);
                setTestNameInfo(json.test_name || '');
                setTestCodeInfo(json.test_code || '');
                setOverallAvg(json.overall_avg);
                setAppliedFromDate(fromDate);   // ADD
                setAppliedToDate(toDate);       // ADD
            }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleClose = () => { window.location.href = "/SysAdmin"; };

    const handleCellClick = (e: React.MouseEvent, lessonCode: string, qnNo: string) => {
        e.stopPropagation();
        setSelectedRowCode(lessonCode);
        const w = 1100, h = 800;
        const left = (window.screen.width / 2) - (w / 2);
        const top = (window.screen.height / 2) - (h / 2);
        const url = famWise
            ? `/SUPPORT/EL_QuestionInsights?DICCode=${selectedDic}&Qnno=${qnNo}&Title=${lessonCode}&TestCode=${testCodeInfo}&Country=${country}&from=Testperformance&Fromlsndate=${appliedFromDate}&Tolsndate=${appliedToDate}`
            : `/LessonBrowser/QuestionViewer?Qnno=${qnNo}&Title=${lessonCode}&TestCode=${testCodeInfo}&Country=${country}&from=Testperformance`;
        window.open(url, "_blank", `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
    };

    return (
        <div style={{ fontFamily: 'Arial', fontSize: '13pt', margin: 0, overflowX: 'hidden' }}>
            {loading && <Loading />}

            {/* ── Teal title bar ── */}
            <div style={{ backgroundColor: 'teal', color: 'white', textAlign: 'center', fontSize: '16pt', padding: '10px' }}>
                TEST PERFORMANCE ANALYSIS
            </div>

            {/* ── Filter bar ── */}
            <div style={{ padding: '10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', backgroundColor: '#f9f9f9', borderBottom: '1px solid #ccc' }}>
                <span>Country :</span>
                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px' }} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="Australia">Australia</option>
                    <option value="Canada">Canada</option>
                </select>

                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', padding: '2px', width: '200px' }} value={selectedDic} onChange={(e) => setSelectedDic(e.target.value)}>
                    <option>--Select--</option>
                    {dics.map(d => <option key={d} value={d}>{d}</option>)}
                </select>

                <select
                    style={{
                        fontFamily: 'Arial',
                        fontSize: '14pt',
                        color: 'blue',
                        padding: '2px',
                        width: '130px'
                    }}
                    value={selectedTest}
                    onChange={(e) => {
                        const value = e.target.value;
                        setSelectedTest(value);
                        setManualTest(value);
                    }}
                >
                    {tests.map(t => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>

                <span>Testcode :</span>
                <input type="text" style={{ fontFamily: 'Arial', fontSize: '14pt', padding: '2px', width: '120px' }} value={manualTest} onChange={(e) => setManualTest(e.target.value)} />

                <span style={{ marginLeft: '6px' }}>From :</span>
                <input type="date" style={{ fontFamily: 'Arial', fontSize: '11pt', padding: '2px' }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />

                <span>TO :</span>
                <input type="date" style={{ fontFamily: 'Arial', fontSize: '11pt', padding: '2px' }} value={toDate} onChange={(e) => setToDate(e.target.value)} />

                <select style={{ fontFamily: 'Arial', fontSize: '14pt', color: 'blue', height: '28px' }} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}>
                    {[100, 90, 80, 70, 60, 50, 40, 30, 20, 10].map(v => <option key={v} value={v}>{v}</option>)}
                </select>

                <button style={{ fontFamily: 'Arial', fontSize: '12pt', cursor: 'pointer', padding: '2px 10px' }} onClick={handleGo}>Go</button>

                <label style={{ fontSize: '14px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={famWise} onChange={(e) => setFamWise(e.target.checked)} />
                    {' '}Family-wise Wrong Answers
                </label>

                <button style={{ fontFamily: 'Arial', fontSize: '14pt', cursor: 'pointer', padding: '4px 15px', marginLeft: 'auto' }} onClick={handleClose}>CLOSE</button>
            </div>

            {/* ── TestCode / TestName + "Question Nos And Marks" header ──
                Matches VB: two lines centered, light blue bg, then "Question Nos And Marks" below */}
            {hasSearched && !loading && gridData.length > 0 && (
                <div style={{
                    backgroundColor: colors.headBg,
                    color: colors.headText,
                    textAlign: 'center',
                    padding: '5px 0',
                    fontFamily: 'Arial',
                    fontSize: '13px',
                    borderBottom: `1px solid ${colors.border}`
                }}>
                    <div>
                        <strong>TestCode: {testCodeInfo}</strong>
                        &nbsp;&nbsp;&nbsp;
                        <strong>TestName: {testNameInfo}</strong>
                    </div>
                    <div style={{ height: "1px", backgroundColor: "#80a0ff", marginTop: "10px", marginBottom: "10px" }}></div>
                    <div style={{ fontWeight: 'bold', marginTop: '2px' }}>Question Nos And Marks</div>
                </div>
            )}

            {/* ── Grid ── */}
            <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '700px', width: '100%' }}>
                {hasSearched && gridData.length === 0 && !loading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>No data available for this test in the selected date range.</div>
                ) : (
                    <table style={{ borderCollapse: 'collapse', width: 'max-content', fontSize: '13px', fontFamily: 'Arial' }}>
                        <thead>
                            {/* Row 1 — fixed columns + spanning "Question Nos And Marks" */}
                            <tr ref={firstHeaderRowRef}>
                                <th style={{ ...thBase, minWidth: '30px' }} rowSpan={2}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: '100px' }} rowSpan={2}>LessonCode</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: '220px' }} rowSpan={2}>LessonName</th>
                                <th style={{ ...thBase, minWidth: '80px' }} rowSpan={2}>Av lsnScore</th>
                                <th colSpan={MAX_QUESTIONS} style={{ ...thBase, textAlign: 'center' }}>
                                    Question Nos And Marks
                                </th>
                            </tr>
                            {/* Row 2 — question number sub-headers, top offset measured from row 1 */}
                            <tr>
                                {columns.map(col => (
                                    <th key={col} style={{ ...thSubBase, minWidth: '48px' }}>
                                        {parseInt(col)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {gridData.map((row, idx) => {
                                const isSelected = selectedRowCode === row.lesson_code;
                                const rowBg = isSelected ? colors.rowSelected : (idx % 2 === 0 ? colors.rowEven : colors.rowOdd);
                                const serialNo = idx + 1;

                                return (
                                    <tr key={row.lesson_code} style={{ backgroundColor: rowBg, cursor: 'pointer' }} onClick={() => setSelectedRowCode(row.lesson_code)}>
                                        {/* Serial number */}
                                        <td style={{ ...tdBase, color: '#555' }}>{serialNo}</td>
                                        <td style={{ ...tdBase, textAlign: 'left', fontWeight: 'bold' }}>{row.lesson_code}</td>
                                        <td style={{ ...tdBase, textAlign: 'left' }}>{row.lesson_name}</td>
                                        <td style={{ ...tdBase, fontWeight: 'bold' }}>{row.avg_score}</td>
                                        {columns.map(col => {
                                            const qnStat = row.questions[col];
                                            const isFail = qnStat && qnStat.perc < threshold;
                                            return (
                                                <td
                                                    key={col}
                                                    style={{
                                                        ...tdBase,
                                                        backgroundColor: isFail ? colors.failBg : 'transparent',
                                                        color: isFail ? colors.failText : '#000',
                                                        cursor: qnStat ? 'pointer' : 'default'
                                                    }}
                                                    onClick={(e) => { if (qnStat) handleCellClick(e, row.lesson_code, col); }}
                                                >
                                                    {qnStat ? qnStat.display_text : ''}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}

                            {/* Grand Average Row — serial number = gridData.length + 1 */}
                            {gridData.length > 0 && (
                                <tr style={{ backgroundColor: colors.avgRowBg }}>
                                    <td style={{ ...tdBase, color: colors.avgRowText, fontWeight: 'bold' }}>{gridData.length + 1}</td>
                                    <td style={tdBase}></td>
                                    <td style={{ ...tdBase, textAlign: 'left', fontWeight: 'bold', color: colors.avgRowText }}>Average Test Score</td>
                                    <td style={{ ...tdBase, fontWeight: 'bold', color: colors.avgRowText }}>{overallAvg}</td>
                                    {columns.map(col => <td key={col} style={tdBase}></td>)}
                                </tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default EL_Testperformance;